import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserIcon,
  KeyIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../services/authContext';
import { apiGet, apiPost, apiDelete } from '../../services/api/client';
import { Button } from '../../components/common/Button';
import { ConfirmModal } from '../../components/common/Modal';
import { useLlmCondomToken } from '../../hooks/useLlmCondomToken';
import { useCopyToast } from '../../hooks/useCopyToast';
import { RespectBandBadge } from '../../components/respect/RespectBandBadge';
import { GiveKudosModal } from '../../components/respect/GiveKudosModal';
import { useKudosForUser } from '../../hooks/useKudos';
import { PersonalApiTokensSection } from '../../components/profile/PersonalApiTokensSection';
import { MonkBotSection } from '../../components/profile/MonkBotSection';
import { BotUsageGauges } from '../../components/profile/BotUsageGauges';
import { PersonalEmailSection } from '../../components/profile/PersonalEmailSection';
import { AvatarSection } from '../../components/profile/AvatarSection';
// MKS-393 — self-service birth date + merch sizes.
import {
  MerchSizesSection,
  PersonalDetailsSection,
} from '../../components/profile/PersonalDetailsSection';
import { MerchSizesNudgeBanner } from '../../components/profile/MerchSizesNudgeBanner';
import { ThemeField } from '../../components/profile/ThemeField';
import { formatDistanceToNow } from 'date-fns';

interface TokenResponse {
  data: {
    token: string | null;
    tokenType: string;
  };
}

export function ProfilePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showGiveKudosModal, setShowGiveKudosModal] = useState(false);
  // F24-001 — copy-toast timeouts are auto-cleared on unmount via useCopyToast.
  const { copied, copy: copyToken, reset: resetCopiedToken } = useCopyToast();
  const { copied: installCopied, copy: copyInstall } = useCopyToast();
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [tokenVisible, setTokenVisible] = useState(false);
  // Tokens are stored as SHA-256 hashes (backend cycle 14 F14-001): GET /auth/token
  // returns '***', never the plaintext. The real value exists only in the POST
  // /auth/token response, so we hold it here to display it once after generation.
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const llmCondom = useLlmCondomToken();
  const installCommand = llmCondom.data?.data.installCommand ?? null;

  const handleCopyInstall = () => copyInstall(installCommand);

  // Fetch current token
  const { data: tokenData, isLoading: isLoadingToken } = useQuery({
    queryKey: ['api-token'],
    queryFn: () => apiGet<TokenResponse>('/auth/token'),
  });

  // hasStoredToken: true whenever GET returns a non-null token value (including '***'
  // for legacy hash-only rows). Drives the "rotate / revoke" controls.
  const hasStoredToken = (tokenData?.data?.token ?? null) !== null;

  // I04: GET /auth/token now returns the real decrypted plaintext when token_enc is
  // present. '***' means a legacy hash-only row — can't be re-displayed.
  // generatedToken (freshly minted via POST) takes precedence so the post-generate
  // UX is identical to before.
  const fetchedToken =
    tokenData?.data?.token && tokenData.data.token !== '***' ? tokenData.data.token : null;
  const currentToken = generatedToken ?? fetchedToken;

  const generateTokenMutation = useMutation({
    mutationFn: () => apiPost<TokenResponse>('/auth/token'),
    onSuccess: (res) => {
      setGeneratedToken(res.data?.token ?? null);
      setTokenVisible(true);
      queryClient.invalidateQueries({ queryKey: ['api-token'] });
      resetCopiedToken();
    },
  });

  const revokeTokenMutation = useMutation({
    mutationFn: () => apiDelete<{ data: { success: boolean } }>('/auth/token'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-token'] });
      setGeneratedToken(null);
      setShowRevokeModal(false);
    },
  });

  const handleCopyToken = () => copyToken(currentToken);

  const handleRevokeToken = () => {
    if (currentToken || hasStoredToken) {
      setShowRevokeModal(true);
    }
  };

  // Phase 61 — Respect hooks (called unconditionally per Rules of Hooks; disabled when user is null)
  const profileUserId = user?.id ?? 0;
  const { data: kudosFeed = [] } = useKudosForUser(profileUserId);

  if (!user) return null;

  // Give-kudos button only visible on OTHER users' profiles (defense-in-depth for self-kudos block).
  // ProfilePage currently renders the current user's profile, so this is always false here.
  // When profile pages for other users are added (future), this condition gates the button correctly.
  const isOwnProfile = profileUserId === user.id;

  const initials =
    user.firstName && user.lastName
      ? `${user.firstName[0]}${user.lastName[0]}`
      : user.email[0]?.toUpperCase() || '?';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Profile</h1>

      {/* MKS-393 — the swag nudge, above everything so it is seen. It hides
          itself once any size is set or once the person dismisses it. */}
      <MerchSizesNudgeBanner />

      {/* User Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
            <span className="text-xl font-semibold text-blue-700">{initials}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-semibold text-gray-900">
                {user.firstName && user.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user.email}
              </h2>
              {/* Phase 61 — Respect band badge */}
              <RespectBandBadge userId={profileUserId} />
              {/* Give-kudos button — only visible when viewing someone else's profile */}
              {!isOwnProfile && (
                <Button
                  variant="secondary"
                  onClick={() => setShowGiveKudosModal(true)}
                >
                  Give kudos
                </Button>
              )}
            </div>
            <p className="text-gray-500">{user.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <UserIcon className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Employee Type</p>
              <p className="text-sm font-medium text-gray-900">
                {user.employeeType?.name || 'Not set'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <UserIcon className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Roles</p>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {user.roles.length > 0 ? (
                  user.roles.map((role) => (
                    <span
                      key={role}
                      className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                    >
                      {role}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">No roles</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Photo Card (MKS-203) */}
      <AvatarSection />

      {/* Personal Email Card (quick 260813-iqd — identity info; the address Monk
          matches external accounts against, e.g. LinkedIn OneCLI connections) */}
      <PersonalEmailSection />

      {/* MKS-393 — self-service birth date and merch sizes. Both write
          PATCH /api/auth/me/personal-details, which is self-scope only. */}
      <PersonalDetailsSection />
      <MerchSizesSection />

      {/* MKS-493 — the Sparkle theme switcher. */}
      <ThemeField />

      {/* API Token Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <KeyIcon className="h-6 w-6 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">API Token</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Use an API token to authenticate with the Monksheets API. Tokens expire after 7 days of inactivity.
          You can use the token in the <code className="bg-gray-100 px-1 rounded">Authorization</code> header
          as <code className="bg-gray-100 px-1 rounded">Bearer {'<token>'}</code>.
        </p>

        {isLoadingToken ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : currentToken ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              Tohle je tvůj API token — můžeš si ho kdykoli zobrazit znovu na téhle stránce. Pro jistotu si ho ale zkopíruj.
            </div>
            <div className="flex items-center gap-2">
              <input
                type={tokenVisible ? 'text' : 'password'}
                readOnly
                value={currentToken}
                className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono text-gray-900"
              />
              <Button
                variant="secondary"
                onClick={() => setTokenVisible((v) => !v)}
                className="flex items-center gap-2"
                aria-label={tokenVisible ? 'Hide token' : 'Show token'}
                aria-pressed={tokenVisible}
              >
                {tokenVisible ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="secondary"
                onClick={handleCopyToken}
                className="flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <CheckIcon className="h-4 w-4 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="h-4 w-4" />
                    Copy
                  </>
                )}
              </Button>
            </div>

            <div className="flex gap-2">
              <Button variant="danger" onClick={handleRevokeToken}>
                Revoke Token
              </Button>
              <Button
                onClick={() => generateTokenMutation.mutate()}
                disabled={generateTokenMutation.isPending}
              >
                {generateTokenMutation.isPending ? 'Regenerating...' : 'Regenerate Token'}
              </Button>
            </div>
          </div>
        ) : hasStoredToken ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Token je nastavený, ale z bezpečnostních důvodů ho nelze znovu zobrazit. Pokud ho
              potřebuješ, vygeneruj nový — starý tím přestane platit.
            </p>
            <div className="flex gap-2">
              <Button variant="danger" onClick={handleRevokeToken}>
                Revoke Token
              </Button>
              <Button
                onClick={() => generateTokenMutation.mutate()}
                disabled={generateTokenMutation.isPending}
              >
                {generateTokenMutation.isPending ? 'Regenerating...' : 'Regenerate Token'}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            onClick={() => generateTokenMutation.mutate()}
            disabled={generateTokenMutation.isPending}
          >
            {generateTokenMutation.isPending ? 'Generating...' : 'Generate New Token'}
          </Button>
        )}

        {generateTokenMutation.isError && (
          <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            Failed to generate token. Please try again.
          </div>
        )}
      </div>

      {/* Personal API Tokens Section (Phase 71, D-08 — additive, legacy card above untouched) */}
      <PersonalApiTokensSection />

      {/* Monk (Slack bot) Section (Phase 73, D-13 — connection status/rotate/disconnect) */}
      <MonkBotSection />

      {/* Claude usage gauges (Phase 87, D-08 — self-view session/weekly gauges + stat tiles) */}
      <BotUsageGauges />

      {/* LLM Condom Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <KeyIcon className="h-6 w-6 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">LLM Condom</h2>
          {user.hasLLMCondom ? (
            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
              On
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              Off
            </span>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Route your local Claude Code through the corporate proxy. Click below to mint a personal
          token and copy the one-liner — run it once in your terminal.
        </p>

        {installCommand ? (
          <div className="space-y-3">
            <pre className="px-3 py-3 bg-gray-900 text-gray-100 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
              {installCommand}
            </pre>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={handleCopyInstall} className="flex items-center gap-2">
                {installCopied ? (
                  <>
                    <CheckIcon className="h-4 w-4 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="h-4 w-4" />
                    Copy command
                  </>
                )}
              </Button>
              <Button
                variant="secondary"
                onClick={() => llmCondom.mutate(undefined)}
                disabled={llmCondom.isPending}
              >
                {llmCondom.isPending ? 'Regenerating...' : 'Regenerate'}
              </Button>
            </div>
            {llmCondom.data?.data.note && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                {llmCondom.data.data.note}
              </p>
            )}
          </div>
        ) : (
          <Button onClick={() => llmCondom.mutate(undefined)} disabled={llmCondom.isPending}>
            {llmCondom.isPending ? 'Generating...' : 'Get install command'}
          </Button>
        )}

        {llmCondom.isError && (
          <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            Failed to mint token. Try again, or contact an admin if it keeps failing.
          </div>
        )}
      </div>

      {/* API Documentation Link */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">API Documentation</h2>
        <p className="text-sm text-gray-600 mb-4">
          Explore the API endpoints and test them using Swagger UI.
        </p>
        <a
          href="/api/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          Open API Documentation
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>
      </div>

      {/* Phase 61 — Kudos feed */}
      {kudosFeed.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Kudos received</h2>
          <ul className="space-y-3">
            {kudosFeed.slice(0, 10).map((kudos) => (
              <li
                key={kudos.id}
                className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-800">{kudos.giver.name}</span>
                  <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                    {kudos.tag.label}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{kudos.message}</p>
                <p className="mt-1 text-xs text-gray-400">
                  {formatDistanceToNow(new Date(kudos.createdAt), { addSuffix: true })}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Revoke Confirmation Modal */}
      <ConfirmModal
        isOpen={showRevokeModal}
        onClose={() => setShowRevokeModal(false)}
        onConfirm={() => revokeTokenMutation.mutate()}
        title="Revoke API Token"
        message="Are you sure you want to revoke this token? Any applications using this token will no longer be able to authenticate."
        confirmText="Revoke Token"
        variant="danger"
        isLoading={revokeTokenMutation.isPending}
      />

      {/* Phase 61 — Give kudos modal (only rendered when viewing someone else's profile) */}
      {showGiveKudosModal && !isOwnProfile && (
        <GiveKudosModal
          recipientUserId={profileUserId}
          recipientName={
            user.firstName && user.lastName
              ? `${user.lastName} ${user.firstName}`
              : user.email
          }
          onClose={() => setShowGiveKudosModal(false)}
        />
      )}
    </div>
  );
}
